import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

type JsonEditorProps = {
  /** The current parsed value — the component stringifies it for display. */
  value: unknown
  /**
   * Called whenever the user edits the text. Fires on every keystroke with the
   * current raw text and either the parsed value (on success) or a parse error.
   */
  onChange: (change: {
    text: string
    parsed: unknown
    error: string | null
    isValid: boolean
  }) => void
  /** Read-only mode — disables the textarea. */
  readOnly?: boolean
  /** Minimum visible height in rows (default 14). */
  rows?: number
  /** Optional id so labels can link to it. */
  id?: string
  /** Extra class names for the wrapper. */
  className?: string
  /**
   * Force-reset the textarea's local text to the stringified ``value`` when
   * this prop changes. Use a counter (e.g. increment on "Reload from server")
   * rather than the value itself so that we don't fight the user's edits.
   */
  resetSignal?: number
}

/**
 * A minimal JSON editor built on a monospace ``<textarea>``.
 *
 * Deliberately stays light: no syntax highlighting, no autocomplete, no
 * new dependencies. Power users editing deeply nested configs through Raw
 * mode will be familiar with this shape (every CI config editor, Kubernetes
 * dashboard, etc. looks the same). The Raw Confirm dialog does the heavy
 * lifting for visualisation via react-syntax-highlighter's diff mode.
 *
 * Validation strategy:
 * - On every keystroke, try to ``JSON.parse`` the current text.
 * - If parsing succeeds, fire ``onChange`` with the parsed value and
 *   ``isValid: true``.
 * - If parsing fails, fire ``onChange`` with the raw text plus the error
 *   message — the parent is responsible for disabling the Save button.
 */
export function JsonEditor({
  value,
  onChange,
  readOnly = false,
  rows = 14,
  id,
  className,
  resetSignal,
}: JsonEditorProps) {
  const { t } = useTranslation()
  const initialText = useMemo(() => {
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return ""
    }
  }, [value])

  const [text, setText] = useState<string>(initialText)
  const [error, setError] = useState<string | null>(null)

  // Reset local text when the parent bumps resetSignal (e.g., after a reload)
  // or when we switch to editing a different resource.
  useEffect(() => {
    setText(initialText)
    setError(null)
  }, [initialText, resetSignal])

  const validate = useCallback(
    (next: string) => {
      if (next.trim() === "") {
        const message = t("adminDashboard.jsonEditor.errorEmpty", {
          defaultValue: "JSON cannot be empty",
        })
        setError(message)
        onChange({ text: next, parsed: null, error: message, isValid: false })
        return
      }
      try {
        const parsed = JSON.parse(next)
        setError(null)
        onChange({ text: next, parsed, error: null, isValid: true })
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t("adminDashboard.jsonEditor.errorInvalid", { defaultValue: "Invalid JSON" })
        setError(message)
        onChange({ text: next, parsed: null, error: message, isValid: false })
      }
    },
    [onChange, t]
  )

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = event.target.value
    setText(next)
    validate(next)
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      <textarea
        aria-invalid={error !== null}
        id={id}
        readOnly={readOnly}
        rows={rows}
        spellCheck={false}
        value={text}
        className={cn(
          "border-input placeholder:text-muted-foreground flex w-full rounded-md border bg-transparent px-3 py-2 font-mono text-xs leading-relaxed shadow-sm focus-visible:ring-1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          "focus-visible:ring-primary",
          error ? "border-destructive focus-visible:ring-destructive" : null,
          readOnly ? "bg-muted/30" : null
        )}
        onChange={handleChange}
      />
      {error ? (
        <p className="text-destructive text-xs" role="alert">
          {error}
        </p>
      ) : (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.jsonEditor.helperText", {
            defaultValue:
              "JSON is validated on every keystroke. Save is disabled until parsing succeeds.",
          })}
        </p>
      )}
    </div>
  )
}
